import React, { useState, useContext, useEffect } from 'react';

import { Modal, Button, Toast,
			Accordion, AccordionContext
		} from 'react-bootstrap';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';

import LexVerb from './LexVerb';
import LexNominal from './LexNominal';
import LexParticiple from './LexParticiple';

import '../css/blocks.css';
import '../css/Components.css';

// Toast for interface errors
const InterfaceError = ({ showInterfaceError, setShowInterfaceError }) => (
	<div className='toastContainer' hidden={!showInterfaceError}>
		<Toast show={showInterfaceError} bg='primary' className='toast'
			onClose={() => setShowInterfaceError(false)}
		>
			<Toast.Header closeButton={true}>
				<p className='m-auto'>Incomplete lexical category input</p>
			</Toast.Header>
			<Toast.Body className='toastBody'>Select an option for each category</Toast.Body>
		</Toast>
	</div>
);

const TAB_KEYS = { Verb: '1', Nominal: '2', Participles: '3' };

const LexicalInterface = props => {
	// Must match the first accordion panel (Verb). A mismatch made submit
	// validate Nominal/Participles while the user was filling Verb fields.
	const [type, setType] = useState('Verb');

	// Lexical (part-of-speech) inputs collected from
	// LexVerb / LexNominal / LexParticiple.
	//   Verb tab:                 pos, transitivity, class, voice
	//   Nominal / Participle tab: lexical_category
	const [lexInputParts, setLexInputParts] = useState({ pos: 'v' });

	const [showInterfaceError, setShowInterfaceError] = useState(false);

	// Reset form each time the modal opens.
	useEffect(() => {
		if (!props.show) return;
		setType('Verb');
		setLexInputParts({ pos: 'v' });
		setShowInterfaceError(false);
	}, [props.show]);

	const [interfaceBtn, setInterfaceBtn] = useState(null);
	useEffect(() => {
		if (!interfaceBtn) return;
		const handler = (e) => handleKeyDown(e);
		window.addEventListener('keydown', handler);
		return () => window.removeEventListener('keydown', handler);
	}, [interfaceBtn]);

	const handleKeyDown = (event) => {
		if (event.key === 'Enter') {
			event.preventDefault();
			interfaceBtn.click();
		}
	};

	// Submit button validation.
	// Note: 'class' uses `in` rather than truthiness because the "unknown"
	// option is a valid selection with an empty-string value ("").
	const isLexDisabled = () => {
		if (type === 'Verb') {
			return !(lexInputParts['transitivity']
				&& 'class' in lexInputParts
				&& lexInputParts['voice']);
		}
		if (type === 'Nominal' || type === 'Participles') {
			return !lexInputParts['lexical_category'];
		}
		return true;
	};

	// Build the lexical_category string and write it to the active row.
	const handleSubmit = async () => {
		if (isLexDisabled()) {
			setShowInterfaceError(true);
			return;
		}

		let input = '';
		if (type === 'Verb') {
			const pos = lexInputParts['pos'] || 'v';
			input = `${pos}${lexInputParts['transitivity']}${lexInputParts['class']}${lexInputParts['voice']}`;
		} else {
			input = lexInputParts['lexical_category'];
		}

		const values = props.ansstate.map((row, i) =>
			i === props.index ? { ...row, lexical_category: input } : row
		);
		props.setans(values);
		props.onHide();
	};

	const CustomToggle = ({ btnPart, eventKey, callback }) => {
		const currentEventKey = useContext(AccordionContext);
		const isCurrentEventKey = currentEventKey.activeEventKey === eventKey;
		const decoratedOnClick = useAccordionButton(eventKey,
			() => callback && callback(eventKey),
		);

		return (
			<Button key={btnPart} variant="outline-primary"
				active={isCurrentEventKey}
				onClick={() => {
					decoratedOnClick();
					if (btnPart !== type) {
						setType(btnPart);
						setShowInterfaceError(false);
						setLexInputParts(btnPart === 'Verb' ? { pos: 'v' } : {});
					}
				}}>
				{btnPart}
			</Button>
		);
	};

	return (
		<>
			<Modal
				{...props}
				size="lg"
				centered
			>
				<InterfaceError showInterfaceError={showInterfaceError} setShowInterfaceError={setShowInterfaceError} />

				<Accordion activeKey={TAB_KEYS[type]}>

					<Modal.Header closeButton>
						<div className="widthVW interfaceTitle">
							{props.title}
						</div>
					</Modal.Header>

					<Modal.Body>
						<div className="widthVW mb-2">
							<CustomToggle key={1} btnPart="Verb" eventKey={TAB_KEYS.Verb} />
							<CustomToggle key={2} btnPart="Nominal" eventKey={TAB_KEYS.Nominal} />
							<CustomToggle key={3} btnPart="Participles" eventKey={TAB_KEYS.Participles} />
						</div>
						<Accordion.Collapse key={1} eventKey={TAB_KEYS.Verb}>
							<LexVerb inputParts={lexInputParts} setInputParts={setLexInputParts} />
						</Accordion.Collapse>
						<Accordion.Collapse key={2} eventKey={TAB_KEYS.Nominal}>
							<LexNominal inputParts={lexInputParts} setInputParts={setLexInputParts} />
						</Accordion.Collapse>
						<Accordion.Collapse key={3} eventKey={TAB_KEYS.Participles}>
							<LexParticiple inputParts={lexInputParts} setInputParts={setLexInputParts} />
						</Accordion.Collapse>
					</Modal.Body>

					<Modal.Footer>
						<Button variant="primary"
							ref={node => setInterfaceBtn(node)}
							onClick={handleSubmit}
						>
							Submit
						</Button>
						<Button onClick={() => props.onHide()} variant="outline-danger">Close</Button>
					</Modal.Footer>

				</Accordion>
			</Modal>
		</>
	);
};

export default LexicalInterface;
